import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { GET_PEOPLE_COUNT } from '../../graphql/query/constants';
import { useSLUGName } from '../../hooks/useHistory';
import { RESTClient } from '../client';
import { transformAllFilters } from '../../utilities/transformAllFilters';
import { makeLogData } from '../../utilities/helpers';

const logData = makeLogData();
function transformData<K extends string>(data: any): any[] {
  const counts = data.counts || {};
  const means = data.means || {};
  const sds = data.sds || {};
  const maxs = data.maxs || {};
  const mins = data.mins || {};

  const df_outliers = data.df_outliers || {};
  // const dataPoints = data.dataPoints || {};
  const significance = data.significance || {};
  const bubbleChart = data.bubble_charts || {};

  const phases: K[] = [];
  const groups: string[] = [];
  const subgroups: string[] = [];

  Object.keys(means).forEach((key) => {
    const phaseName = key as K;
    if (!phases.includes(phaseName)) phases.push(phaseName);
    Object.keys(means[phaseName] || {}).forEach((groupName) => {
      if (!groups.includes(groupName)) groups.push(groupName);
      Object.keys(means[phaseName]?.[groupName] || {}).forEach((subgroupName) => {
        if (!subgroups.includes(subgroupName)) subgroups.push(subgroupName);
      });
    });
  });

  const result: any[] = [];
  // Phase: Always Modality
  // Subgroup: Compare (Split)
  // Group: Category (Across)
  phases.map((phase) => {
    const group_res = subgroups.map((group, gg) => {
      const context = groups.map((subgroup) => {
        const averageDuration = means?.[phase]?.[subgroup]?.[group] as number;
        const activitiesAmount = counts?.[phase]?.[subgroup]?.[group] as number;

        // Only robotic SDS should be displayed
        const standardDeviation =
          phase === 'robotic' ? (sds?.[phase]?.[subgroup]?.[group] as number) : 0;
        const min = mins?.[phase]?.[subgroup]?.[group] as number;
        const max = maxs?.[phase]?.[subgroup]?.[group] as number;

        const significanceData = significance?.[subgroup];

        return {
          name: `${phase} ${group}`,
          label: `${phase} ${group} ${subgroup}`,
          averageDuration,
          activitiesAmount,
          standardDeviation,
          max,
          min,
          dataPoints: [],
          FStatistic: significanceData?.['F-statistic'] || 0,
          pValue: significanceData?.['p-value'] || 0,
          significant: !!significanceData?.significant,
          backgroundColor: phase === 'robotic' ? '#FF4444' : '#4444FF',
          raw: data,
        };
      });
      // return { name: group === '0' ? 'robotic-OR0004' : `${phase}-${group}`, context };
      return { name: `${phase}-${group}`, context };
    });

    result.push(...group_res);
  });

  return result;
}

async function makeQueryUtilization<K extends string>(params: Record<string, any>) {
  const { data } = await RESTClient.get<AnalyticsGenericResponse<K>>('/utilization', { params });

  const result = Object.keys(data || {}).reduce((acc, key) => {
    if (['means', 'maxs', 'mins', 'counts', 'sds', 'significance', 'bubble_charts'].includes(key)) {
      const obj = data[key as keyof AnalyticsGenericResponse<K>];
      const x = Object.keys(obj || {}).reduce((phases, phaseName) => {
        return { ...phases, [phaseName.replace(/_mean|_max|_min/g, '')]: (obj as any)[phaseName] };
      }, {});

      return { ...acc, [key]: x };
    }

    return { ...acc, [key]: data[key as keyof AnalyticsGenericResponse<K>] };
  }, {}) as AnalyticsGenericResponse<K>;

  return result;
}

export function useQueryUtilization<K extends string>(
  variables: QueryAnalyticsArgs & { aspect_ratio?: number },
  pcView?: string | null,
  keepPreviousData?: boolean,
) {
  const organization = useSLUGName();
  const params = useMemo(
    () => ({
      from: variables.from,
      to: variables.to,
      ...(organization ? { organization } : {}),
      ...(variables.aspect_ratio ? { aspect_ratio: variables.aspect_ratio } : {}),
      split: variables.split || undefined,
      category: variables.category || undefined,
      stackedBy: variables.stackedBy || undefined,
      normalization: variables.normalization || undefined,
      ...(pcView ? { activity_group: pcView } : {}),
      ...(variables.filters || {}),
    }),
    [variables],
  );

  const processor = useCallback(() => makeQueryUtilization(params), [params]);
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery([GET_PEOPLE_COUNT, params], processor, {
    keepPreviousData,
  });

  logData('/utilization(' + JSON.stringify(params) + ')', data);

  const result = useMemo(() => {
    const res = transformData<K>((data || {}) as AnalyticsGenericResponse<K>);
    const arr = [...res];
    const restIndex = arr.findIndex((item) => /rest/i.test(item.name));
    const restItem = arr.splice(restIndex, 1);
    arr.push(...restItem);

    return arr;
  }, [data]);

  const allFilters = useMemo(() => transformAllFilters(data?.all_filters), [data?.all_filters]);

  return { data: result, rawData: data, allFilters, loading, refetch };
}
